import {observer} from "mobx-react";
import * as React from "react";
import {withAppStores} from "../../../../AppStores";
import type {TransportLayer} from "../../../../data/TransportLayer";
import type {Portfolio} from "../../../../data/models/Portfolio";
import type {AppState} from "../../../../data/state/AppState";
import {Permission, FieldDataType} from "../../../../generated/api/base";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {FieldV5} from "../../details/FieldV5";
import {ClickToEditInputV5} from "../../input/clicktoedit/ClickToEditInputV5";
import {SelectInputV5} from "../../input/select/SelectInputV5";
import type {Type} from "../../../../data/models/Type";
import {ConfirmWindowV5} from "../../popup/ConfirmWindowV5";

interface IPortfolioDefaultFieldsProps {
	readonly item: Portfolio;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

interface IPortfolioDefaultFieldsState {
	isUpdating: boolean;
	name: string;
}

@withAppStores("appState", "transport")
@observer
export class PortfolioDefaultFieldsV5 extends React.Component<IPortfolioDefaultFieldsProps, IPortfolioDefaultFieldsState> {
	constructor(props: IPortfolioDefaultFieldsProps) {
		super(props);
		this.state = {
			isUpdating: false,
			name: this.props.item.name,
		};
		this._lastValidName = props.item.name;
	}
	private _lastValidName: string;

	public static getDerivedStateFromProps(props: IPortfolioDefaultFieldsProps, state: IPortfolioDefaultFieldsState) {
		if (props.item.name !== state.name) {
			return {
				name: props.item.name,
			};
		}
		return null;
	}

	private getErrorMessage = () => {
		const {appState, item} = this.props;
		const {name} = this.state;

		return !name.trim() ? "Name cannot be empty!" : appState.actions.isPortfolioNameValid(name, item.id) ? "" : "Name needs to be unique!";
	};

	private onTypeChange = async (type: Type) => {
		const {item} = this.props;

		const title = "Confirm Portfolio Type Change";
		const message =
			"Once you change the portfolio type, fields (and data) not assigned to the new portfolio type will be removed. Do you wish to continue?";
		const config = {
			ok: "Change",
			cancel: "Cancel",
		};

		const confirmed = await ConfirmWindowV5.open(message, title, config);

		if (confirmed) {
			item.typeId = type.id;
			await this.props.transport.updatePortfolioType(item);
		}
	};

	private onBlurNameInput = async () => {
		const {item, transport} = this.props;
		const {name} = this.state;

		item.name = name;

		if (!this.getErrorMessage()) {
			this.setState({isUpdating: true});
			await TimeUtils.waitUpdate(transport.appState.actions.updateFields([item], {name}), transport.appState.app.notificationContainer);
			this._lastValidName = name;
			this.setState({isUpdating: false});
		} else {
			item.name = this._lastValidName;
			this.setState({name: this._lastValidName});
		}
	};

	private onInput = (name: string) => {
		this.setState({name});
		this.props.item.name = name;
	};

	private get permission() {
		return this.props.appState.actions.getPortfolioPermission(this.props.item.id);
	}

	public override render() {
		const {item, appState} = this.props;
		const {isUpdating} = this.state;
		const hasPermission = this.permission >= Permission.Update;

		return (
			<>
				<FieldV5 label={appState.actions.getRefIdName(item.ownFeature)}>{item.refId}</FieldV5>
				<FieldV5
					className="TypeSelector"
					label="Type"
				>
					{hasPermission ? (
						<SelectInputV5
							options={this.props.appState.types[item.ownFeature]}
							renderOptionWhenClosed={(item) => item.name}
							selected={appState.actions.getTypeById(item.typeId)}
							onChange={this.onTypeChange}
							isSameWidth={true}
						/>
					) : (
						appState.actions.getTypeById(item.typeId).name
					)}
				</FieldV5>
				<FieldV5
					label="Portfolio Name"
					className="stucked"
				>
					{hasPermission ? (
						<ClickToEditInputV5
							value={item.name}
							onLiveChange={this.onInput}
							getErrorMessage={this.getErrorMessage}
							onBlur={this.onBlurNameInput}
							dataType={FieldDataType.SingleLineText}
							isUpdating={isUpdating}
						/>
					) : (
						item.name
					)}
				</FieldV5>
			</>
		);
	}
}
